import {noop} from '@benchling/js-utils';
import {Group} from '@visx/group';
import {scaleLinear} from '@visx/scale';
import {Bar, Line} from '@visx/shape';
import React, {useMemo, SVGProps} from 'react';

import {TooltipWrapper} from 'ui-library/bs/Tooltip';

interface TooltipBarGraphProps<ValueType extends number | null> {
  values: Array<ValueType>;
  maxValue: number;
  xFn: (index: number) => number;
  maxHeight: number;
  getFormattedTooltipTitle?: (value: ValueType) => string | null;
  tooltipDelay?: TooltipOptions['delay'];
  baselineProps?: SVGProps<SVGLineElement>;
  barRectProps?: SVGProps<SVGRectElement>;
  onClickCallback?: (index: number) => void;
}

/**
 * Render numeric data as an SVG bar chart, optionally with a tooltip for each bar.
 *
 * @param baselineProps Additional props for the SVG line element rendered as a baseline
 * @param barRectProps Additional props for each SVG rect element representing each bar of data
 * @param values Numeric data (with nulls treated as 0) in the range [0, maxValue]
 * @param maxValue The maximum possible numeric value (i.e. the value at which a full-height bar is rendered)
 * @param xFn Linear scaling function to map data index to an X position within the SVG
 * @param maxHeight Height of the bar chart or how much vertical space this element will render within.
 * @param getFormattedTooltipTitle Optional function to format a value as the title of a tooltip.
 * @param tooltipDelay Optional settings for delaying the tooltip appearing (see Bootstrap TooltipOptions).
 * @param onClickCallback Optional callback invoked with the data index when clicking on a data bar.
 */
export default function TooltipBarGraph<ValueType extends number | null>({
  baselineProps,
  barRectProps,
  values,
  maxValue,
  xFn,
  maxHeight,
  getFormattedTooltipTitle,
  tooltipDelay,
  onClickCallback = noop,
}: TooltipBarGraphProps<ValueType>): JSX.Element {
  const width = xFn(1) - xFn(0);
  const verticalScale = useMemo(
    () => scaleLinear<number>().domain([0, maxValue]).range([0, maxHeight]),
    [maxValue, maxHeight]
  );
  return (
    <Group>
      {values.map((value, index) => {
        const effectiveValue: number = value ?? 0;
        return (
          <TooltipWrapper
            key={index}
            // Note that a null/undefined value for the title causes TooltipWrapper to skip rendering a Tooltip entirely.
            tooltipTitle={getFormattedTooltipTitle?.(value)}
            tooltipDelay={tooltipDelay}
          >
            <Bar
              {...barRectProps}
              onClick={() => {
                onClickCallback?.(index);
              }}
              x={xFn(index)}
              y={maxHeight - verticalScale(effectiveValue)}
              height={verticalScale(effectiveValue)}
              width={width}
            />
          </TooltipWrapper>
        );
      })}
      <Line {...baselineProps} from={{x: xFn(0), y: maxHeight}} to={{x: xFn(values.length), y: maxHeight}} />
    </Group>
  );
}
